import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table'
import moment from 'moment'
import momenttz from '../../helpers/momenttz'
import momentUtcToUserTz from '../../helpers/momentUtcToUserTz'
import { toastr } from 'react-redux-toastr'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import CalendarDropdown from "../CalendarDropdown";
import CustomButton from '../../CustomButton';

import { tableExportOptions } from '../../helpers/table'
import CustomVideoRequest from './CustomVideoRequest'

import './LiveRecordingLogs.sass'
import { getDeviceAvailableVideo, getDeviceRecordingLogs } from "../../../action/DeviceActions";
import { getRecordingLogGaps } from "../../../action/ReportsActions";

const LiveRecordingLogs = (props) => {
	const dispatch = useDispatch();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.editAssetContainer.configModal.liveRecordingLogs' });
	const { device, loaderModal } = props;

	const [intervalState, setIntervalState] = useState(null);
	const [intervalFnc, setIntervalFnc] = useState(null);
	const [logsDate, setLogsDate] = useState(moment().format('YYYYMMDD'));
	const [logs, setLogs] = useState([]);
	const [number_gaps, setNumber_gaps] = useState(0);
	const [recording_time_missing, setRecording_time_missing] = useState(0);
	const [avg_time_between_gaps, setAvg_time_between_gaps] = useState(0);
	const [avg_gap_length, setAvg_gap_length] = useState(0);
	const [rec_stops_num, setRec_stops_num] = useState(0);
	const [isSelect, setIsSelect] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const [selectedLogs, setSelectedLogs] = useState([]);
	const [showVideoRequest, setShowVideoRequest] = useState(false);
	const [availableItems, setAvailableItems] = useState([]);

	useEffect(() => {
		getRecordingLogs();
		getRecordingLogsOnline();
	}, []);

	useEffect(() => {
		return () => {
			if (intervalState) {
				clearInterval(intervalState);
				setIntervalState(null);
				localStorage.removeItem("currentDate");
			}
		}
	}, [intervalState]);

	useEffect(() => {
		return () => {
			if (intervalFnc) {
				clearInterval(intervalFnc);
				setIntervalFnc(null);
			}
		}
	}, [intervalFnc]);

	localStorage.setItem("currentDate", `${logsDate}`);

	const toggleLoaderModal = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER_MODAL', payload: show });
	}, [dispatch]);

	const getDayRangeUtc = (date) => {
		const day = moment(date).format('YYYY-MM-DD')
		return {
			start_ts: momenttz(`${day} 00:00:00`).utc().format('YYYYMMDDHHmmss'),
			end_ts: momenttz(`${day} 23:59:59`).utc().format('YYYYMMDDHHmmss'),
		}
	}

	const getRecordingLogs = (dateArg = null) => {
		setIsSelect(false);
		setSelectAll(false);
		setSelectedLogs([]);
		// FWSD-5246
		const params = {
			device_ids: device.device_id,
			...getDayRangeUtc(dateArg || moment(logsDate)),
		}

		const requestArray = []
		requestArray.push(getDeviceRecordingLogs({ ...params }))
		requestArray.push(getRecordingLogGaps({ ...params }))

		toggleLoaderModal(true);
		Promise.all(requestArray)
			.then(res => {
				const { logs } = res[0].data.response
				const { number_gaps, recording_time_missing, avg_time_between_gaps, avg_gap_length, rec_stops_num } = res[1].data.response.report.length && res[1].data.response.report[0]

				setLogs(logs);
				setNumber_gaps(number_gaps);
				setRecording_time_missing(recording_time_missing);
				setAvg_time_between_gaps(avg_time_between_gaps);
				setAvg_gap_length(avg_gap_length);
				setRec_stops_num(rec_stops_num);

				toggleLoaderModal(false);
			})
			.catch(error => {
				toggleLoaderModal(false);
				console.log('Promise.all error', error.response, error)
				toastr.error(t('issueGettingRecordingLogs'))
			})
	}

	// FWSD-5096
	const getRecordingLogsOnline = () => {
		if (intervalState) {
			clearInterval(intervalState)
			setInterval(null)
		}

		setIntervalState(setInterval(() => {
			const logsDate = localStorage.getItem("currentDate");
			const params = {
				device_ids: device.device_id,
				...getDayRangeUtc(moment(logsDate, 'YYYYMMDD')),
			}

			const requestArray = []
			requestArray.push(getDeviceRecordingLogs({ ...params }))
			requestArray.push(getRecordingLogGaps({ ...params }))

			Promise.all(requestArray)
				.then(res => {
					const { logs } = res[0].data.response
					const { number_gaps, recording_time_missing, avg_time_between_gaps, avg_gap_length, rec_stops_num } = res[1].data.response.report.length && res[1].data.response.report[0]

					setLogs(logs);
					setNumber_gaps(number_gaps);
					setRecording_time_missing(recording_time_missing);
					setAvg_time_between_gaps(avg_time_between_gaps);
					setAvg_gap_length(avg_gap_length);
					setRec_stops_num(rec_stops_num);
				})
				.catch(error => {
					console.log('Promise.all online error: ', error.response, error)
				})
		}, 10000))
	}

	const changeLogsDate = (selectedDate) => {
		if (moment(selectedDate).isAfter(moment())) {
			toastr.info(t('noFutureLogs'))
		}
		else {
			setLogsDate(moment(selectedDate).format('YYYYMMDD'))
			getRecordingLogs(moment(selectedDate))
		}

		if (intervalFnc) {
			clearInterval(intervalFnc);
			setIntervalFnc(null);
		}
	}

	const getNextLogs = () => {
		const selectedDate = moment(logsDate).add(1, 'days')
		if (selectedDate.isAfter(moment())) {
			toastr.info(t('noFutureLogs'))
		}
		else {
			setLogsDate(selectedDate.format('YYYYMMDD'))
			getRecordingLogs(selectedDate)
		}

		if (intervalFnc) {
			clearInterval(intervalFnc);
			setIntervalFnc(null);
		}
	}

	const getPrevLogs = () => {
		const selectedDate = moment(logsDate).subtract(1, 'days');
		setLogsDate(selectedDate.format('YYYYMMDD'));
		getRecordingLogs(selectedDate);

		if (intervalFnc) {
			clearInterval(intervalFnc);
			setIntervalFnc(null);
		}
	}

	const timestampFormatter = (cell, row) => {
		if (cell) {
			return momentUtcToUserTz(cell).format('HH:mm:ss')
		}

		return '';
	}

	const timestampFormatterCSV = (cell, row) => {
		if (cell) {
			return momentUtcToUserTz(cell).format('YYYY-MM-DD HH:mm:ss')
		}

		return '';
	}

	const inputFormatter = (cell, row, format, i) => (<input type="checkbox" className="select_log" disabled={!availableItems.includes(i)} checked={availableItems.includes(i) ? cell : false} onChange={e => handleSelectLog(e.target.checked, i)} />);

	const handleSelectAll = (checked) => {
		if (availableItems.length) {
			setSelectAll(checked);
		}
		if (checked) {
			setSelectedLogs(availableItems);
		} else {
			setSelectedLogs([]);
		}
		setLogs(logs.map(e => { e.selected = checked; return e }));
	}

	const handleSelectLog = (checked, i) => {
		let data = logs.reduce((acc, e, i) => {
			if (e.selected) acc.push(i);
			return acc;
		}, []);
		if (checked) {
			if (!data.length || (i === data[0] - 1) || (i === data[data.length - 1] + 1)) {
				data.push(i);
				setLogs(logs.map((e, ind) => { if (ind === i) e.selected = true; return e }));
				if (availableItems.length === data.length) {
					setSelectAll(true);
				}
			} else {
				return
			}
		} else {
			if ((i === data[0]) || (i === data[data.length - 1])) {
				data = data.filter(e => e !== i);
				setLogs(logs.map((e, ind) => { if (ind === i) e.selected = false; return e }));
				if (availableItems.length !== data.length) {
					setSelectAll(false);
				}
			} else {
				return
			}
		}
		setSelectedLogs(data.sort((a, b) => a - b));
	}

	const onBtnSelect = () => {
		if (isSelect) {
			clearInterval(intervalFnc);
			setIntervalFnc(null);
			setSelectAll(false);
			setSelectedLogs([]);
			setLogs(logs.map(e => { e.selected = false; return e }));
		} else {
			getAvailableVideos();
			setIntervalFnc(setInterval(getAvailableVideos, 10000));
		}
		setIsSelect(!isSelect);
	}

	const getAvailableVideos = () => {
		const params = {
			device_id: device.device_id,
			start_time: momenttz(moment(logsDate).format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss'),
			end_time: momenttz(moment(logsDate).format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss')
		}
		getDeviceAvailableVideo(params)
			.then(res => {
				let { available_video } = res.data.response[0]
				let pos = available_video.indexOf('3')
				while (pos > -1) {
					if (pos === 0) {
						available_video = available_video.substring(0, pos) + '0' + available_video.substring(pos)
					}
					else {
						available_video = available_video.substring(0, pos) + available_video[pos - 1] + available_video.substring(pos + 1)
					}
					pos = available_video.indexOf('3')
				}

				let data = logs.reduce((acc, e, i) => {
					if (!!+available_video[moment.duration(momentUtcToUserTz(e.start_ts).format('HH:mm')).asMinutes()]) acc.push(i);
					return acc;
				}, []);
				setAvailableItems(data.sort((a, b) => a - b));
			})
			.catch(error => {
				console.log('getAvailableVideos error: ', error.response, error)
				let errDescription = i18n.t('errorMessage.errDescription');
				if (error.response.data.response.error) {
					errDescription = error.response.data.response.error
				}
				toastr.error(errDescription)
			})
	}

	selectedLogs.length && logs.map((e, i) => (e.selected = selectedLogs.includes(i) ? true : false));
	const start_time = selectedLogs.length ? momentUtcToUserTz(logs[selectedLogs[0]].start_ts) : null;
	const end_time = selectedLogs.length ? momentUtcToUserTz(logs[selectedLogs[selectedLogs.length - 1]].end_ts) : null;

	const onBtnRequestVideo = () => {
		if (end_time.diff(start_time, 'minutes') <= 60) {
			setShowVideoRequest(true);
		} else {
			toastr.error(t('videoRequestTimeLimitError'));
		}
	}

	if (showVideoRequest) {
		return (
			<CustomVideoRequest
				propsSelectedDevice={device}
				propsSelectedDate={moment(logsDate)}
				propsStartTime={start_time.format('HH:mm:ss')}
				propsEndTime={end_time.format('HH:mm:ss')}
				onSave={() => setShowVideoRequest(false)}
				onHide={() => setShowVideoRequest(false)}
			/>
		)
	}

	return (
		<div className="live-recording-logs">
			<h4 className="category-title text-center">{t('titleLogs')}</h4>
			<div className="logs-body">
				<div className="live-logs-date-container">
					<div className="datetime">
						<svg xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" fill="currentColor" className="bi bi-caret-left-fill" viewBox="0 0 16 16"
							onClick={getPrevLogs}
						>
							<path d="M3.86 8.753l5.482 4.796c.646.566 1.658.106 1.658-.753V3.204a1 1 0 0 0-1.659-.753l-5.48 4.796a1 1 0 0 0 0 1.506z" />
						</svg>
						<CalendarDropdown
							date={moment(logsDate)}
							disabledDay={date => !moment(date).isBefore(moment())}
							onChange={changeLogsDate}
						/>
						<svg xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" fill="currentColor" className="bi bi-caret-right-fill" viewBox="0 0 16 16"
							onClick={getNextLogs}
						>
							<path d="M12.14 8.753l-5.482 4.796c-.646.566-1.658.106-1.658-.753V3.204a1 1 0 0 1 1.659-.753l5.48 4.796a1 1 0 0 1 0 1.506z" />
						</svg>
					</div>
				</div>
				{isSelect && (
					<label>
						<input type="checkbox" className="select_all" checked={selectAll} onChange={e => handleSelectAll(e.target.checked)} />
						<span>{t('selectAll')}</span>
					</label>)}
			</div>

			<div className="table-logs">
				<div className="live-logs-container">
					{logs.length > 0
						? (
							<BootstrapTable
								data={logs}
								{...tableExportOptions}
								pagination={false}
								csvFileName={`recordinglogs_${device.device_id}_${moment(logsDate).format('MM_DD_YYYY')}.csv`}
							>
								{isSelect ? (
									<TableHeaderColumn
										width="5%"
										dataField="selected"
										headerTitle={false}
										dataFormat={inputFormatter}
									>
									</TableHeaderColumn>) : null}
								<TableHeaderColumn
									width="15%"
									dataField="start_ts"
									headerTitle={false}
									dataFormat={timestampFormatter}
									csvHeader={t('headerTime')}
									csvFormat={timestampFormatterCSV}
									isKey
								>
									{t('headerTime')}
								</TableHeaderColumn>
								<TableHeaderColumn
									width="85%"
									dataField="filename"
									headerTitle={false}
									csvHeader={t('headerFilename')}
								>
									{t('headerFilename')}
								</TableHeaderColumn>
							</BootstrapTable>
						) : (
							<div className="text-center">
								{t('noLogsForDate')}
							</div>
						)
					}
				</div>
				{logs.length ? (
					<div className="btn-groups">
						<CustomButton
							variant="primary-outline"
							onClick={onBtnSelect}
						>
							{isSelect ? t('btnUnselect') : t('btnSelect')}
						</CustomButton>
						<CustomButton
							variant="primary"
							disabled={!selectedLogs.length}
							onClick={onBtnRequestVideo}
						>
							{t('btnRequestVideo')}
						</CustomButton>
					</div>) : null}
			</div>

			<hr />

			<h4 className="category-title text-center">{t('statForDate')}</h4>
			<ul className="no-style-list">
				<li className="statistic-line">{t('number_gaps')}<span>{number_gaps || 0}</span></li>
				<li className="statistic-line">{t('rec_stops_num')}<span>{rec_stops_num || 0}</span></li>
				<li className="statistic-line">{t('rec_time_missing')}<span>{recording_time_missing || 0}</span></li>
				<li className="statistic-line">{t('avg_time_between_gaps')}<span>{avg_time_between_gaps || 0}</span></li>
				<li className="statistic-line">{t('avg_gap_length')}<span>{avg_gap_length || 0}</span></li>
			</ul>
			{loaderModal}
		</div>
	)
}

LiveRecordingLogs.propTypes = {
	device: PropTypes.objectOf(PropTypes.any).isRequired,
}

export default LiveRecordingLogs;
